import { Center, Flex, IconButton, Image } from '@chakra-ui/react';
import { ELEMENT_CREATED, GIF_CREATED } from 'constants/mixpanel';
import { useInfiniteScroll } from 'hooks/useInfiniteScrollHook';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { addNewWidget, getInitialWidgetPosition, useAppDispatch } from 'modules/Editor/store';
import { selectElementsWidgetMenuIndex } from 'modules/Editor/store/editorSettingsSelector';
import { useAppSelector } from 'modules/Editor/store/hooks';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { CSSProperties, memo, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeGrid } from 'react-window';
import { WidgetType } from 'types/widget.types';
import { LottiePlayer } from 'widgets/LottieWidget/LottiePlayer';
import { generateLottieDefaultData } from 'widgets/LottieWidget/LottieWidget.helper';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { COLUMN_NUM, GUTTER_SIZE, INDEX_OFFSET, THUMBNAIL_WIDTH } from '../../../InfiniteGrid/InfiniteGrid.config';
import { ElementTypes } from '../../Elements/Elements.types';
import { ElementsPackTypes } from '../../ElementsMenu.types';
import { DEFAULT_LOTTIE_SIZE_HEIGHT, DEFAULT_LOTTIE_SIZE_WIDTH, PROVIDED_BY_GUTTER } from './GifsWidgetPack.config';
import { createLottieWidgetData } from './GifsWidgetPack.helper';
import { LottieMetadata } from './GifsWidgetPack.types';

// Props required by react-window
// @see https://github.com/bvaughn/react-window/blob/master/src/createGridComponent.js
interface CellProps<T> {
  columnIndex: number;
  data: T;
  isScrolling?: boolean;
  rowIndex: number;
  style: CSSProperties;
}

interface GridInterface {
  hits: LottieMetadata[];
  onLoadMoreAnchor: (instance: Element | null) => void;
  disableDragDrop?: boolean;
}

interface InfiniteListProps<T = any> {
  data: T[];
  numberOfItems: number;
  loadMore: () => void;
  disableDragDrop?: boolean;
}

export const InfiniteList = memo(
  ({ data, numberOfItems, loadMore, disableDragDrop }: InfiniteListProps<LottieMetadata>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [, onLoadAnchor] = useInfiniteScroll({ onLoadMore: loadMore, onLoadThreshold: 0.8 });

    // Update the infinite scroll box size when the window is resizing
    useResizeObserver({
      ref: containerRef,
      onResize: ({ width = 0, height = 0 }) => {
        setContainerSize({ width, height });
      },
    });

    const gridData = useMemo(() => {
      return {
        onLoadMoreAnchor: onLoadAnchor,
        hits: data,
        disableDragDrop,
      };
    }, [data, disableDragDrop, onLoadAnchor]);

    return (
      <Flex
        ref={containerRef}
        direction='column'
        h='full'
        overflow='auto'
        marginTop={3}
        data-testid={'infinite-gifs-list'}
        gap={2}
      >
        <FixedSizeGrid<GridInterface>
          columnCount={COLUMN_NUM}
          columnWidth={THUMBNAIL_WIDTH + GUTTER_SIZE}
          itemData={gridData}
          height={containerSize.height}
          width={containerSize.width}
          rowHeight={DEFAULT_LOTTIE_SIZE_HEIGHT + GUTTER_SIZE + PROVIDED_BY_GUTTER}
          rowCount={Math.ceil(numberOfItems / 2)}
          style={{ overflowX: 'hidden' }}
        >
          {Cell}
        </FixedSizeGrid>
      </Flex>
    );
  },
);
InfiniteList.displayName = 'InfiniteList';

export const Cell = ({ columnIndex, rowIndex, style, data }: CellProps<GridInterface>) => {
  const index = rowIndex * COLUMN_NUM + columnIndex;
  const { hits, onLoadMoreAnchor, disableDragDrop } = data;
  const hit = hits[index];
  const dispatch = useAppDispatch();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.gifsMenu' });
  const activeElementWidgetMenu = useAppSelector(selectElementsWidgetMenuIndex);

  const activeWidgets = useAppSelector(selectActiveWidgets) || {};
  const widgetId = activeWidgets[0]?.id;
  const widgetType = widgetId && getWidgetTypeFromId(widgetId);

  if (!hit) return null;

  const onClick = () => {
    if (widgetType === WidgetType.Lottie && activeElementWidgetMenu === ElementsPackTypes.GifsReplace) {
      dispatch(updateWidget({ widgetId, widgetData: { url: hit.file } }));
    } else {
      const { top, left } = getInitialWidgetPosition();
      const widgetData = generateLottieDefaultData(hit.file, top, left);
      dispatch(addNewWidget(widgetData));
    }

    Mixpanel.track(ELEMENT_CREATED.eventName, {
      from: ELEMENT_CREATED.from.LeftPanel,
      element_id: ELEMENT_CREATED.elementsId.gifs,
      element_type: ElementTypes.GIFS,
    });
  };

  const isAnchorCell = index === hits.length - INDEX_OFFSET;

  return (
    <Flex
      // Set unique key for each item to avoid re-rendering when the data array is changed
      key={`${index}-${hit.id}-${isAnchorCell}`}
      // Set load more anchor ref near the end of the list
      ref={isAnchorCell ? onLoadMoreAnchor : undefined}
      style={style}
      justifyContent={'flex-start'}
      alignItems={'center'}
    >
      <WidgetDragDropWrapper
        isDraggable={!disableDragDrop}
        widgetData={createLottieWidgetData(hit)}
        type={WidgetType.Image}
        dragImage={
          <Flex w={DEFAULT_LOTTIE_SIZE_WIDTH} h={DEFAULT_LOTTIE_SIZE_HEIGHT}>
            <Center>
              <Image src={hit.preview_url} w='full' h='full' objectFit='contain' pointerEvents='none' />
            </Center>
          </Flex>
        }
        dragImageOffset={{ x: DEFAULT_LOTTIE_SIZE_WIDTH / 2, y: DEFAULT_LOTTIE_SIZE_HEIGHT / 2 }}
        trackingEventData={{
          event_name: GIF_CREATED.eventName,
          properties: {
            from: GIF_CREATED.from.Search,
          },
        }}
      >
        <Flex direction='column'>
          <IconButton
            aria-label={hit.title}
            w='full'
            h='full'
            onClick={onClick}
            variant={'icon-btn-side-menu'}
            padding={2}
            pb={4}
          >
            <LottiePlayer src={hit.file} width={DEFAULT_LOTTIE_SIZE_WIDTH} height={DEFAULT_LOTTIE_SIZE_HEIGHT} />
          </IconButton>
          <Flex
            position={'absolute'}
            pl={1}
            bottom={0}
            w='fit-content'
            alignItems={'center'}
            fontSize={10}
            borderRadius='sm'
          >
            {t('authorCredit.by')} {hit.animator_name}
          </Flex>
        </Flex>
      </WidgetDragDropWrapper>
    </Flex>
  );
};
